import React, { lazy, Suspense, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { Toaster } from 'sonner';
import { toast } from 'sonner';
import { usePlatform } from '../platform/PlatformContext';
import { SiteHeader } from '../site/Site';
import { api } from '../platform/api';
import { unlockDcSound, playDcReveal } from './dcSound';
import './deadcenter.css';

const DeadCenter = lazy(() => import('./DeadCenter'));
const DeadCenterLeaderboard = lazy(() => import('./DeadCenterLeaderboard'));

const TOAST_OPTIONS = { className: 'dc-toast' };

function ShellLoader({ forLeaderboard = false }) {
  return (
    <div className="dc-root" data-testid="dc-shell-loader">
      <main className="platform-page">
        <span className="platform-eyebrow">
          {forLeaderboard ? 'TODAY\'S DEAD CENTER LEADERBOARD' : 'FIVE SHAPES. ONE TAP EACH. FIND THE CENTER.'}
        </span>
        <h1>Dead Center</h1>
        <div className="dc-loading-wrap" style={{ padding: '14px 0', margin: '20px 0 0' }}>
          <div className="dc-spinner dc-shell-spinner" aria-hidden="true" />
          <span>{forLeaderboard ? 'Loading leaderboard…' : 'Today\'s challenge is on its way…'}</span>
        </div>
      </main>
    </div>
  );
}

/**
 * DcAppShell
 * Dedicated game shell for /deadcenter/daily and /deadcenter/leaderboard.
 * Uses the global SiteHeader so the navigation is identical site-wide.
 * Sound state is managed here and passed down to DeadCenter via props.
 * Preview-only reset control lives in its own small floating bar.
 */
export default function DcAppShell() {
  const { view, scope } = usePlatform();

  const [sound, setSound] = useState(() => {
    try { return localStorage.getItem('dead-center.sound') === 'true'; } catch { return false; }
  });
  const [resetting, setResetting] = useState(false);
  /* Live AVG accuracy during gameplay \u2014 lifted from DeadCenter child */
  const [headerAvg, setHeaderAvg] = useState(null);

  const toggleSound = () => {
    const enabled = !sound;
    setSound(enabled);
    try { localStorage.setItem('dead-center.sound', String(enabled)); } catch { /* Optional preference. */ }
    if (enabled) { unlockDcSound(); playDcReveal(); }
  };

  const handleReset = async () => {
    if (resetting) return;
    setResetting(true);
    try {
      await api('/deadcenter/reset', { method: 'POST' });
      toast.success('Progress reset \u2014 play again!');
      window.location.reload();
    } catch (err) {
      toast.error(err.message || 'Reset failed.');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="dc-shell" data-testid="dc-app-shell">

      {/* \u2500\u2500 Global site header (CubitPlay logo + drawer + account) \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */}
      <SiteHeader/>

      {/* \u2500\u2500 Shell body \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */}
      <div className="dc-shell-body" data-testid="dc-shell-body">

        {/* Preview-only reset button — compact floating strip, hidden in production */}
        {scope === 'preview' && (
          <div className="dc-preview-controls" data-testid="dc-preview-controls">
            <button
              className="dc-icon-btn dc-reset-btn"
              data-testid="dc-preview-reset"
              aria-label="Reset today's attempt (preview only)"
              title="Reset my progress (preview only)"
              onClick={handleReset}
              disabled={resetting}
            >
              <RotateCcw size={15} />
              <span>Reset (preview)</span>
            </button>
          </div>
        )}

        {view === '/deadcenter/daily' && (
          <Suspense fallback={<ShellLoader />}>
            <DeadCenter
              sound={sound}
              onAvgChange={setHeaderAvg}
              onToggleSound={toggleSound}
            />
          </Suspense>
        )}
        {view === '/deadcenter/leaderboard' && (
          <Suspense fallback={<ShellLoader forLeaderboard />}>
            <DeadCenterLeaderboard />
          </Suspense>
        )}
      </div>

      <Toaster position="top-center" closeButton toastOptions={TOAST_OPTIONS} />
    </div>
  );
}
